/**
 * Issue #972: TTL（有効期限）対応のlocalStorageユーティリティ
 * Issue #1149: Legacy形式データの自動クリア対応
 *
 * localStorageに保存するデータにタイムスタンプと有効期限を付与し、
 * 古いキャッシュが残り続ける問題を防止する。
 *
 * 重要（Issue #1149）:
 * TTL形式でない古いデータ（legacy format）は自動的に削除される。
 * これにより、別ユーザーでログインした際に古い組織データが残り続け、
 * 新規ユーザーが誤って /subscription-required に誘導されるバグを防止。
 *
 * セキュリティ注意:
 * このTTLはクライアント側の利便性向上（UX改善）を目的としており、
 * セキュリティ保護のためのものではない。localStorageはユーザーが
 * 自由に編集可能なため、権限・決済状態の判定は必ずサーバー側で行う。
 * （hasSubscription, hasOrganization等はAPIレスポンスで検証）
 */

import logger from './logger';

/**
 * TTL付きデータの型
 */
interface StorageDataWithTTL<T> {
  value: T;
  timestamp: number;
  expiresAt: number;
}

/**
 * デフォルトのTTL（24時間）
 */
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * TTL判定時のバッファ（1分）
 * Issue #1279: TTL境界付近での不安定性を防止
 * ネットワーク遅延やクロック同期のズレを考慮
 */
const TTL_BUFFER_MS = 60 * 1000;

/**
 * TTL付きでlocalStorageに保存
 *
 * @param key - ストレージキー
 * @param value - 保存する値
 * @param ttlMs - 有効期限（ミリ秒）。デフォルトは24時間
 */
export function setWithTTL<T>(
  key: string,
  value: T,
  ttlMs: number = DEFAULT_TTL_MS
): void {
  try {
    // Issue #1279: Math.floorで整数化し、浮動小数点誤差を防止
    const now = Math.floor(Date.now());
    const ttl = Math.floor(Math.max(0, ttlMs)); // 負の値を防止
    const data: StorageDataWithTTL<T> = {
      value,
      timestamp: now,
      expiresAt: now + ttl,
    };
    localStorage.setItem(key, JSON.stringify(data));
    logger.debug(`[StorageUtils] Saved ${key} with TTL ${ttl}ms`);
  } catch (error) {
    logger.error(`[StorageUtils] Failed to save ${key}:`, error);
  }
}

/**
 * TTLを考慮してlocalStorageから取得
 *
 * 有効期限が切れている場合はnullを返し、キーを削除する
 *
 * @param key - ストレージキー
 * @returns 有効な値、または期限切れ/存在しない場合はnull
 */
export function getWithTTL<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const data = JSON.parse(raw);

    // Issue #1149: TTL形式でない古いデータ（legacy format）は削除する
    // 後方互換性を維持すると、別ユーザーでログインした際に古いデータが残り続け、
    // 新規ユーザーが誤って /subscription-required に誘導されるバグが発生する
    if (!('expiresAt' in data)) {
      logger.warn(
        `[StorageUtils] ${key} is in legacy format (no TTL), removing stale data`
      );
      localStorage.removeItem(key);
      return null;
    }

    const ttlData = data as StorageDataWithTTL<T>;

    // Issue #1279: バッファを考慮した有効期限チェック
    // 整数化して浮動小数点誤差を防止
    const now = Math.floor(Date.now());
    if (now + TTL_BUFFER_MS > ttlData.expiresAt) {
      logger.info(
        `[StorageUtils] ${key} has expired (or within buffer), removing`
      );
      localStorage.removeItem(key);
      return null;
    }

    return ttlData.value;
  } catch (error) {
    logger.error(`[StorageUtils] Failed to get ${key}:`, error);
    return null;
  }
}

/**
 * TTL付きデータが有効かどうかをチェック（データは取得しない）
 *
 * @param key - ストレージキー
 * @returns 有効な場合true、期限切れまたは存在しない場合false
 */
export function isValidTTL(key: string): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return false;
    }

    const data = JSON.parse(raw);

    // Issue #1149: TTL形式でない古いデータ（legacy format）は無効とみなす
    if (!('expiresAt' in data)) {
      logger.warn(
        `[StorageUtils] ${key} is in legacy format (no TTL), treating as invalid`
      );
      return false;
    }

    // Issue #1279: バッファを考慮した有効期限チェック
    const now = Math.floor(Date.now());
    return (
      now + TTL_BUFFER_MS <= (data as StorageDataWithTTL<unknown>).expiresAt
    );
  } catch {
    return false;
  }
}

/**
 * 組織情報用のキー
 */
export const ORGANIZATION_STORAGE_KEYS = {
  ORGANIZATIONS: 'ripia_organizations',
  USER_ROLES: 'ripia_user_roles',
} as const;

/**
 * 組織情報をTTL付きで保存
 *
 * @param organizations - 組織情報の配列
 * @param ttlMs - 有効期限（ミリ秒）。デフォルトは24時間
 */
export function saveOrganizations(
  organizations: unknown[],
  ttlMs: number = DEFAULT_TTL_MS
): void {
  setWithTTL(ORGANIZATION_STORAGE_KEYS.ORGANIZATIONS, organizations, ttlMs);
}

/**
 * 組織情報を取得（TTLチェック付き）
 *
 * @returns 有効な組織情報、または期限切れ/存在しない場合はnull
 */
export function getOrganizations<T = unknown[]>(): T | null {
  return getWithTTL<T>(ORGANIZATION_STORAGE_KEYS.ORGANIZATIONS);
}

/**
 * ユーザー権限をTTL付きで保存
 *
 * @param userRoles - ユーザー権限の配列
 * @param ttlMs - 有効期限（ミリ秒）。デフォルトは24時間
 */
export function saveUserRoles(
  userRoles: unknown[],
  ttlMs: number = DEFAULT_TTL_MS
): void {
  setWithTTL(ORGANIZATION_STORAGE_KEYS.USER_ROLES, userRoles, ttlMs);
}

/**
 * ユーザー権限を取得（TTLチェック付き）
 *
 * @returns 有効なユーザー権限、または期限切れ/存在しない場合はnull
 */
export function getUserRoles<T = unknown[]>(): T | null {
  return getWithTTL<T>(ORGANIZATION_STORAGE_KEYS.USER_ROLES);
}

/**
 * Issue #2059: localStorage後方互換のtenantId解決
 *
 * localStorageに保存されたユーザーロールは、リネーム前のデータでは
 * `organizationId` フィールドを持つ場合がある。このヘルパーは
 * `tenantId` を優先しつつ、旧フィールド名にフォールバックする。
 *
 * @param role - localStorageから取得したユーザーロールオブジェクト
 * @returns tenantId（旧 organizationId からのフォールバック含む）
 */
export function resolveTenantId(role: {
  tenantId?: string;
  [key: string]: unknown;
}): string | undefined {
  const id = role.tenantId ?? (role as Record<string, unknown>).organizationId;
  if (id == null) return undefined;
  if (typeof id !== 'string') {
    logger.error(`[resolveTenantId] Expected string, got ${typeof id}:`, id);
    return undefined;
  }
  return id;
}

/**
 * 組織関連のキャッシュをすべてクリア
 *
 * ログアウト時やセッション終了時に呼び出し、
 * 組織情報・権限・テナントコンテキストを一括削除する。
 */
export function clearOrganizationCache(): void {
  localStorage.removeItem(ORGANIZATION_STORAGE_KEYS.ORGANIZATIONS);
  localStorage.removeItem(ORGANIZATION_STORAGE_KEYS.USER_ROLES);
  logger.info(
    '[StorageUtils] Organization cache cleared (organizations, userRoles)'
  );
}

/**
 * Issue #1149: Legacy形式のストレージデータをクリーンアップ
 *
 * 組織関連のキーに対してgetWithTTL()を呼び出し、
 * TTL形式でない古いデータ（legacy format）を自動的に削除する。
 *
 * 用途:
 * - アプリ起動時に古いデータを一括クリーンアップ
 * - 定期的なメンテナンス処理
 *
 * @returns クリーンアップされたキーの数
 */
export function cleanupLegacyStorageData(): number {
  const keysToCheck = [
    ORGANIZATION_STORAGE_KEYS.ORGANIZATIONS,
    ORGANIZATION_STORAGE_KEYS.USER_ROLES,
  ];

  let cleanedCount = 0;

  keysToCheck.forEach((key) => {
    const raw = localStorage.getItem(key);
    if (raw) {
      try {
        const data = JSON.parse(raw);
        // TTL形式でないデータを直接削除（ログ重複を避けるため）
        if (!('expiresAt' in data)) {
          localStorage.removeItem(key);
          cleanedCount++;
          logger.info(`[StorageUtils] Cleaned up legacy data: ${key}`);
        }
      } catch {
        // JSONパースエラーの場合も削除
        localStorage.removeItem(key);
        cleanedCount++;
        logger.warn(`[StorageUtils] Removed invalid JSON data: ${key}`);
      }
    }
  });

  if (cleanedCount > 0) {
    logger.info(
      `[StorageUtils] Legacy data cleanup completed: ${cleanedCount} keys cleaned`
    );
  }

  return cleanedCount;
}

// ============================================================
// Issue #1246: ユーザースコープストレージ
// 異なるユーザーが同じブラウザでログインした際に、
// 前のユーザーのデータが混在することを防止する。
// ============================================================

/**
 * ユーザースコープのストレージキーを生成
 *
 * @param baseKey - 基本キー（例: 'ripia_organizations'）
 * @param userId - ユーザーID
 * @returns ユーザースコープのキー（例: 'ripia_organizations_user123'）
 */
export function getUserScopedKey(baseKey: string, userId: string): string {
  return `${baseKey}_${userId}`;
}

/**
 * ユーザースコープで組織情報を保存
 *
 * @param userId - ユーザーID
 * @param organizations - 組織情報の配列
 * @param ttlMs - 有効期限（ミリ秒）。デフォルトは24時間
 */
export function saveOrganizationsForUser(
  userId: string,
  organizations: unknown[],
  ttlMs: number = DEFAULT_TTL_MS
): void {
  const key = getUserScopedKey(ORGANIZATION_STORAGE_KEYS.ORGANIZATIONS, userId);
  setWithTTL(key, organizations, ttlMs);
  logger.debug(`[StorageUtils] Saved organizations for user ${userId}`);
}

/**
 * ユーザースコープで組織情報を取得
 *
 * @param userId - ユーザーID
 * @returns 有効な組織情報、または期限切れ/存在しない場合はnull
 */
export function getOrganizationsForUser<T = unknown[]>(
  userId: string
): T | null {
  const key = getUserScopedKey(ORGANIZATION_STORAGE_KEYS.ORGANIZATIONS, userId);
  return getWithTTL<T>(key);
}

/**
 * ユーザースコープでユーザー権限を保存
 *
 * @param userId - ユーザーID
 * @param userRoles - ユーザー権限の配列
 * @param ttlMs - 有効期限（ミリ秒）。デフォルトは24時間
 */
export function saveUserRolesForUser(
  userId: string,
  userRoles: unknown[],
  ttlMs: number = DEFAULT_TTL_MS
): void {
  const key = getUserScopedKey(ORGANIZATION_STORAGE_KEYS.USER_ROLES, userId);
  setWithTTL(key, userRoles, ttlMs);
  logger.debug(`[StorageUtils] Saved user roles for user ${userId}`);
}

/**
 * ユーザースコープでユーザー権限を取得
 *
 * @param userId - ユーザーID
 * @returns 有効なユーザー権限、または期限切れ/存在しない場合はnull
 */
export function getUserRolesForUser<T = unknown[]>(userId: string): T | null {
  const key = getUserScopedKey(ORGANIZATION_STORAGE_KEYS.USER_ROLES, userId);
  return getWithTTL<T>(key);
}

/**
 * ユーザースコープで組織関連のキャッシュをすべてクリア
 *
 * @param userId - ユーザーID
 */
export function clearOrganizationCacheForUser(userId: string): void {
  const keys = [
    ORGANIZATION_STORAGE_KEYS.ORGANIZATIONS,
    ORGANIZATION_STORAGE_KEYS.USER_ROLES,
  ];
  keys.forEach((baseKey) => {
    const scopedKey = getUserScopedKey(baseKey, userId);
    localStorage.removeItem(scopedKey);
  });
  logger.info(`[StorageUtils] Organization cache cleared for user ${userId}`);
}

/**
 * Issue #1246: グローバルキーからユーザースコープキーへマイグレーション
 *
 * 既存のグローバルキー（ripia_organizations等）に保存されたデータを
 * ユーザースコープキー（ripia_organizations_{userId}）に移行する。
 *
 * マイグレーション後、グローバルキーは削除される。
 * これにより、別ユーザーがログインした際に前のユーザーのデータを
 * 参照してしまうバグを防止する。
 *
 * @param userId - 現在ログイン中のユーザーID
 * @returns マイグレーションされたキーの数
 */
export function migrateToUserScopedStorage(userId: string): number {
  let migratedCount = 0;

  // 組織情報のマイグレーション
  const legacyOrgs = getWithTTL<unknown[]>(
    ORGANIZATION_STORAGE_KEYS.ORGANIZATIONS
  );
  if (legacyOrgs && legacyOrgs.length > 0) {
    saveOrganizationsForUser(userId, legacyOrgs);
    localStorage.removeItem(ORGANIZATION_STORAGE_KEYS.ORGANIZATIONS);
    migratedCount++;
    logger.info(
      `[StorageUtils] Migrated organizations to user-scoped storage for user ${userId}`
    );
  }

  // ユーザー権限のマイグレーション
  const legacyRoles = getWithTTL<unknown[]>(
    ORGANIZATION_STORAGE_KEYS.USER_ROLES
  );
  if (legacyRoles && legacyRoles.length > 0) {
    saveUserRolesForUser(userId, legacyRoles);
    localStorage.removeItem(ORGANIZATION_STORAGE_KEYS.USER_ROLES);
    migratedCount++;
    logger.info(
      `[StorageUtils] Migrated user roles to user-scoped storage for user ${userId}`
    );
  }

  if (migratedCount > 0) {
    logger.info(
      `[StorageUtils] Migration completed: ${migratedCount} keys migrated to user-scoped storage`
    );
  }

  return migratedCount;
}

/**
 * Issue #1246: 別ユーザーのグローバルキャッシュをクリア
 *
 * ログイン時に、グローバルキーに残っている別ユーザーのデータを削除する。
 * マイグレーション前のデータが残っている場合の安全策。
 */
export function clearGlobalOrganizationCache(): void {
  localStorage.removeItem(ORGANIZATION_STORAGE_KEYS.ORGANIZATIONS);
  localStorage.removeItem(ORGANIZATION_STORAGE_KEYS.USER_ROLES);
  logger.info('[StorageUtils] Global organization cache cleared');
}

// ============================================================
// Issue #1285: 統一キー命名規則 (ripia:<category>:<key>)
// Issue #1277: マイグレーションロック機構 & クロスタブ同期
// ============================================================

/**
 * 新しい統一キー命名規則
 * フォーマット: ripia:<category>:<key> または ripia:<category>:<key>:<scope>
 */
export const STORAGE_KEY_PREFIX = 'ripia:';

/**
 * カテゴリ定義
 */
export const STORAGE_CATEGORIES = {
  AUTH: 'auth',
  ORG: 'org',
  CACHE: 'cache',
  DEVICE: 'device',
  SETTINGS: 'settings',
  MIGRATION: 'migration',
  CRYPTO: 'crypto',
  AUDIT: 'audit',
  SETUP: 'setup',
  SYSTEM: 'system',
  STORE: 'store',
  AI: 'ai',
  ACCOUNT: 'account',
  TENANT: 'tenant',
} as const;

/**
 * 新しいストレージキー定義（統一命名規則）
 */
export const NEW_STORAGE_KEYS = {
  // Auth系
  AUTH_ACCESS_TOKEN: 'ripia:auth:access_token',
  AUTH_REFRESH_TOKEN: 'ripia:auth:refresh_token',
  AUTH_TOKEN_PAIR: 'ripia:auth:token_pair',
  AUTH_SESSION: 'ripia:auth:session',
  AUTH_JTI_REGISTRY: 'ripia:auth:jti_registry',
  AUTH_TOKEN_BLACKLIST: 'ripia:auth:token_blacklist',
  AUTH_PREVIEW_USER: 'ripia:auth:preview_user',

  // Org系
  ORG_LIST: 'ripia:org:list',
  ORG_ROLES: 'ripia:org:roles',
  ORG_TENANT: 'ripia:org:tenant',

  // Device系
  DEVICE_FINGERPRINT: 'ripia:device:fingerprint',
  DEVICE_PROFILES: 'ripia:device:profiles',
  DEVICE_ID: 'ripia:device:id', // 新規: IndexedDBフォールバック用

  // Settings系
  SETTINGS_PREFERENCES: 'ripia:settings:preferences',
  SETTINGS_BACKUP: 'ripia:settings:backup',
  SETTINGS_CHANGELOG: 'ripia:settings:changelog',

  // Cache系
  CACHE_CUSTOMERS: 'ripia:cache:customers',
  CACHE_RESERVATIONS: 'ripia:cache:reservations',
  CACHE_TREATMENTS: 'ripia:cache:treatments',
  CACHE_MEDICAL_RECORDS: 'ripia:cache:medical_records',
  CACHE_SEQUENCES: 'ripia:cache:sequences',
  CACHE_STAFF_SHIFTS: 'ripia:cache:staff_shifts',
  CACHE_EXTENDED_STAFF_SHIFTS: 'ripia:cache:extended_staff_shifts',
  CACHE_SERVICES: 'ripia:cache:services',

  // Crypto系
  CRYPTO_KEY_ROTATION: 'ripia:crypto:key_rotation',
  CRYPTO_MASTER_KEY: 'ripia:crypto:master_key',
  CRYPTO_CONFIG: 'ripia:crypto:config',

  // Migration系
  MIGRATION_LOCK: 'ripia:migration:lock',
  MIGRATION_DATA_V1: 'ripia:migration:data_v1',
  MIGRATION_KEYS_V2: 'ripia:migration:keys_v2',
  MIGRATION_USER_SCOPE_V1: 'ripia:migration:user_scope_v1',
  MIGRATION_COMPLETED: 'ripia:migration:completed',

  // Audit系
  AUDIT_LOGS: 'ripia:audit:logs',

  // Account系
  ACCOUNT_ID: 'ripia:account:id',
  ACCOUNT_EMAIL: 'ripia:account:email',

  // System系
  SYSTEM_HEALTHCHECK: 'ripia:system:healthcheck',
  SYSTEM_SETUP_COMPLETE: 'ripia:system:setup_complete',

  // Store系 (Zustand)
  STORE_MAIN: 'ripia:store:main',

  // AI系
  AI_CHAT_HISTORY: 'ripia:ai:chat_history',
} as const;

/**
 * 旧キー → 新キー マッピング（一括移行用）
 * Issue #1285: 80+キーの命名規則統一
 */
export const KEY_MIGRATION_MAP: Record<string, string> = {
  // Auth系 — ソースキーは旧salonhub_*名（ブラウザに保存済みのキー名）
  salonhub_jwt_access_token: NEW_STORAGE_KEYS.AUTH_ACCESS_TOKEN,
  salonhub_jwt_refresh_token: NEW_STORAGE_KEYS.AUTH_REFRESH_TOKEN,
  salonhub_jwt_token_pair: NEW_STORAGE_KEYS.AUTH_TOKEN_PAIR,
  salonhub_session_data: NEW_STORAGE_KEYS.AUTH_SESSION,
  salonhub_jti_registry: NEW_STORAGE_KEYS.AUTH_JTI_REGISTRY,
  salonhub_token_blacklist: NEW_STORAGE_KEYS.AUTH_TOKEN_BLACKLIST,
  salonhub_preview_user: NEW_STORAGE_KEYS.AUTH_PREVIEW_USER,

  // Org系
  salonhub_organizations: NEW_STORAGE_KEYS.ORG_LIST,
  salonhub_user_roles: NEW_STORAGE_KEYS.ORG_ROLES,

  // Device系
  salonhub_device_fingerprint: NEW_STORAGE_KEYS.DEVICE_FINGERPRINT,
  salonhub_device_profiles: NEW_STORAGE_KEYS.DEVICE_PROFILES,

  // Settings系（ハイフン→コロン）
  'salonhub-settings': NEW_STORAGE_KEYS.SETTINGS_PREFERENCES,
  'salonhub-settings-backup': NEW_STORAGE_KEYS.SETTINGS_BACKUP,
  'salonhub-settings-changes': NEW_STORAGE_KEYS.SETTINGS_CHANGELOG,

  // Cache系
  salonhub_customers: NEW_STORAGE_KEYS.CACHE_CUSTOMERS,
  salonhub_reservations: NEW_STORAGE_KEYS.CACHE_RESERVATIONS,
  salonhub_treatments: NEW_STORAGE_KEYS.CACHE_TREATMENTS,
  salonhub_medical_records: NEW_STORAGE_KEYS.CACHE_MEDICAL_RECORDS,
  salonhub_sequences: NEW_STORAGE_KEYS.CACHE_SEQUENCES,
  salonhub_staff_shifts: NEW_STORAGE_KEYS.CACHE_STAFF_SHIFTS,
  salonhub_extended_staff_shifts: NEW_STORAGE_KEYS.CACHE_EXTENDED_STAFF_SHIFTS,
  'salonhub-services': NEW_STORAGE_KEYS.CACHE_SERVICES,

  // Crypto系
  salonhub_key_rotation_data: NEW_STORAGE_KEYS.CRYPTO_KEY_ROTATION,
  salonhub_master_key_manager: NEW_STORAGE_KEYS.CRYPTO_MASTER_KEY,
  salonhub_encryption_config: NEW_STORAGE_KEYS.CRYPTO_CONFIG,

  // Migration系
  'salonhub-data-migration-completed': NEW_STORAGE_KEYS.MIGRATION_DATA_V1,
  salonhub_migration_completed: NEW_STORAGE_KEYS.MIGRATION_COMPLETED,

  // Audit系
  salonhub_audit_logs: NEW_STORAGE_KEYS.AUDIT_LOGS,

  // Account系
  salonhub_current_account_id: NEW_STORAGE_KEYS.ACCOUNT_ID,
  salonhub_current_account_email: NEW_STORAGE_KEYS.ACCOUNT_EMAIL,

  // System系
  SALONHUB_COMPLETE_SETUP_v2: NEW_STORAGE_KEYS.SYSTEM_SETUP_COMPLETE,

  // Store系 (Zustand)
  'salonhub-store': NEW_STORAGE_KEYS.STORE_MAIN,

  // AI系
  'salonhub-ai-chat-history': NEW_STORAGE_KEYS.AI_CHAT_HISTORY,
};

/**
 * 暗号化されたキー一覧（強制再ログイン時に削除対象）
 */
export const ENCRYPTED_STORAGE_KEYS = [
  // 旧salonhub_*キー（既存ユーザーのブラウザに残存）
  'salonhub_jwt_access_token',
  'salonhub_jwt_refresh_token',
  'salonhub_jwt_token_pair',
  'salonhub_session_data',
  'salonhub_device_fingerprint',
  'salonhub_jti_registry',
  'salonhub_key_rotation_data',
  'salonhub_master_key_manager',
  'salonhub_encryption_config',
  // 新キー
  NEW_STORAGE_KEYS.AUTH_ACCESS_TOKEN,
  NEW_STORAGE_KEYS.AUTH_REFRESH_TOKEN,
  NEW_STORAGE_KEYS.AUTH_TOKEN_PAIR,
  NEW_STORAGE_KEYS.AUTH_SESSION,
  NEW_STORAGE_KEYS.DEVICE_FINGERPRINT,
  NEW_STORAGE_KEYS.AUTH_JTI_REGISTRY,
  NEW_STORAGE_KEYS.CRYPTO_KEY_ROTATION,
  NEW_STORAGE_KEYS.CRYPTO_MASTER_KEY,
  NEW_STORAGE_KEYS.CRYPTO_CONFIG,
];

// ============================================================
// Issue #1277: マイグレーションロック機構
// ============================================================

/**
 * ロックタイムアウト（5秒）
 */
const MIGRATION_LOCK_TIMEOUT_MS = 5000;

/**
 * マイグレーションロック情報
 */
interface MigrationLock {
  timestamp: number;
  lockedBy: string;
  tabId: string;
}

/**
 * タブ固有のID生成
 */
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

/**
 * マイグレーションロックを取得
 * Issue #1277: 複数タブでのレース条件防止
 *
 * @param userId - ユーザーID
 * @returns ロック取得成功ならtrue
 */
export function acquireMigrationLock(userId: string): boolean {
  try {
    const now = Date.now();
    const existingLockStr = localStorage.getItem(
      NEW_STORAGE_KEYS.MIGRATION_LOCK
    );

    if (existingLockStr) {
      const existingLock: MigrationLock = JSON.parse(existingLockStr);

      // タイムアウト判定
      if (now - existingLock.timestamp < MIGRATION_LOCK_TIMEOUT_MS) {
        // 同一ユーザー・同一タブの場合は再取得を許可
        if (existingLock.lockedBy === userId && existingLock.tabId === TAB_ID) {
          logger.debug(
            `[StorageUtils] Re-acquired migration lock for user ${userId}`
          );
          return true;
        }
        // 他のタブがロック中
        logger.warn(
          `[StorageUtils] Migration lock held by another tab: ${existingLock.tabId}`
        );
        return false;
      }
      // タイムアウト済み、新規ロック取得可能
      logger.info(
        `[StorageUtils] Expired migration lock detected, acquiring new lock`
      );
    }

    // 新規ロック取得
    const newLock: MigrationLock = {
      timestamp: now,
      lockedBy: userId,
      tabId: TAB_ID,
    };
    localStorage.setItem(
      NEW_STORAGE_KEYS.MIGRATION_LOCK,
      JSON.stringify(newLock)
    );
    logger.debug(`[StorageUtils] Migration lock acquired for user ${userId}`);
    return true;
  } catch (error) {
    logger.error('[StorageUtils] Failed to acquire migration lock:', error);
    return false;
  }
}

/**
 * マイグレーションロックを解放
 *
 * @param userId - ユーザーID
 */
export function releaseMigrationLock(userId: string): void {
  try {
    const existingLockStr = localStorage.getItem(
      NEW_STORAGE_KEYS.MIGRATION_LOCK
    );
    if (existingLockStr) {
      const existingLock: MigrationLock = JSON.parse(existingLockStr);
      // 自分のロックのみ解放
      if (existingLock.lockedBy === userId && existingLock.tabId === TAB_ID) {
        localStorage.removeItem(NEW_STORAGE_KEYS.MIGRATION_LOCK);
        logger.debug(
          `[StorageUtils] Migration lock released for user ${userId}`
        );
      }
    }
  } catch (error) {
    logger.error('[StorageUtils] Failed to release migration lock:', error);
  }
}

/**
 * マイグレーション完了フラグをチェック
 *
 * @param userId - ユーザーID
 * @returns 完了済みならtrue
 */
export function isMigrationCompleted(userId: string): boolean {
  try {
    const flagKey = `${NEW_STORAGE_KEYS.MIGRATION_USER_SCOPE_V1}:${userId}`;
    return localStorage.getItem(flagKey) === 'completed';
  } catch {
    return false;
  }
}

/**
 * マイグレーション完了フラグを設定
 *
 * @param userId - ユーザーID
 */
export function markMigrationCompleted(userId: string): void {
  try {
    const flagKey = `${NEW_STORAGE_KEYS.MIGRATION_USER_SCOPE_V1}:${userId}`;
    localStorage.setItem(flagKey, 'completed');
    logger.info(
      `[StorageUtils] Migration marked as completed for user ${userId}`
    );
  } catch (error) {
    logger.error('[StorageUtils] Failed to mark migration completed:', error);
  }
}

// ============================================================
// Issue #1277: クロスタブ同期（StorageEvent）
// ============================================================

/**
 * StorageEvent ハンドラー型
 */
type StorageEventHandler = (event: StorageEvent) => void;

/**
 * 登録済みハンドラー
 */
const storageEventHandlers: Set<StorageEventHandler> = new Set();

/**
 * StorageEventリスナーをセットアップ
 * Issue #1277: クロスタブでのマイグレーション完了通知
 *
 * @param handler - イベントハンドラー
 * @returns クリーンアップ関数
 */
export function setupCrossTabSync(handler: StorageEventHandler): () => void {
  const wrappedHandler = (event: StorageEvent) => {
    // ripia:で始まるキーのみ処理
    if (event.key?.startsWith(STORAGE_KEY_PREFIX)) {
      handler(event);
    }
  };

  window.addEventListener('storage', wrappedHandler);
  storageEventHandlers.add(wrappedHandler);

  logger.debug('[StorageUtils] Cross-tab sync listener registered');

  // クリーンアップ関数を返す
  return () => {
    window.removeEventListener('storage', wrappedHandler);
    storageEventHandlers.delete(wrappedHandler);
    logger.debug('[StorageUtils] Cross-tab sync listener removed');
  };
}

/**
 * マイグレーション完了イベントをハンドリング
 *
 * @param event - StorageEvent
 * @param onMigrationComplete - コールバック
 */
export function handleMigrationEvent(
  event: StorageEvent,
  onMigrationComplete?: (userId: string) => void
): void {
  if (!event.key) return;

  // マイグレーション完了通知
  if (event.key.startsWith(NEW_STORAGE_KEYS.MIGRATION_USER_SCOPE_V1)) {
    const userId = event.key.replace(
      `${NEW_STORAGE_KEYS.MIGRATION_USER_SCOPE_V1}:`,
      ''
    );
    if (event.newValue === 'completed') {
      logger.info(
        `[StorageUtils] Cross-tab migration completed notification for user ${userId}`
      );
      onMigrationComplete?.(userId);
    }
  }

  // ロック解放通知
  if (
    event.key === NEW_STORAGE_KEYS.MIGRATION_LOCK &&
    event.newValue === null
  ) {
    logger.debug('[StorageUtils] Cross-tab migration lock released');
  }
}

// ============================================================
// Issue #1285: キー一括移行
// ============================================================

/**
 * キー移行結果
 */
export interface KeyMigrationResult {
  migrated: number;
  failed: string[];
  skipped: number;
}

/**
 * 全キーを一括移行
 * Issue #1285: 80+キーの命名規則統一
 *
 * @returns 移行結果
 */
export function migrateAllKeysAtOnce(): KeyMigrationResult {
  const result: KeyMigrationResult = {
    migrated: 0,
    failed: [],
    skipped: 0,
  };

  // 既に移行済みかチェック
  if (
    localStorage.getItem(NEW_STORAGE_KEYS.MIGRATION_KEYS_V2) === 'completed'
  ) {
    logger.info('[StorageUtils] Key migration already completed, skipping');
    return result;
  }

  for (const [oldKey, newKey] of Object.entries(KEY_MIGRATION_MAP)) {
    try {
      const value = localStorage.getItem(oldKey);
      if (value !== null) {
        // 新キーに既に値がある場合はスキップ（データ損失防止）
        if (localStorage.getItem(newKey) !== null) {
          result.skipped++;
          logger.debug(
            `[StorageUtils] Skipped key migration (target exists): ${oldKey} → ${newKey}`
          );
          continue;
        }

        localStorage.setItem(newKey, value);
        localStorage.removeItem(oldKey);
        result.migrated++;
        logger.debug(`[StorageUtils] Migrated key: ${oldKey} → ${newKey}`);
      }
    } catch (error) {
      result.failed.push(oldKey);
      logger.error(`[StorageUtils] Failed to migrate key: ${oldKey}`, error);
    }
  }

  // マイグレーション完了フラグを設定
  if (result.failed.length === 0) {
    localStorage.setItem(NEW_STORAGE_KEYS.MIGRATION_KEYS_V2, 'completed');
    logger.info(
      `[StorageUtils] Key migration completed: ${result.migrated} migrated, ${result.skipped} skipped`
    );
  } else {
    logger.warn(
      `[StorageUtils] Key migration completed with errors: ${result.migrated} migrated, ${result.failed.length} failed`
    );
  }

  return result;
}

/**
 * キー移行が必要かチェック
 *
 * @returns 移行が必要ならtrue
 */
export function isKeyMigrationNeeded(): boolean {
  // 既に移行完了している場合
  if (
    localStorage.getItem(NEW_STORAGE_KEYS.MIGRATION_KEYS_V2) === 'completed'
  ) {
    return false;
  }

  // 旧キーが存在するかチェック
  for (const oldKey of Object.keys(KEY_MIGRATION_MAP)) {
    if (localStorage.getItem(oldKey) !== null) {
      return true;
    }
  }

  return false;
}

// ============================================================
// 強制再ログイン（既存暗号化データ破棄）
// ============================================================

/**
 * 暗号化データをすべてクリア
 * Issue #1278: フィンガープリント変更時の強制再ログイン
 *
 * @returns クリアされたキーの数
 */
export function clearAllEncryptedData(): number {
  let clearedCount = 0;

  for (const key of ENCRYPTED_STORAGE_KEYS) {
    try {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        clearedCount++;
      }
    } catch (error) {
      logger.error(
        `[StorageUtils] Failed to clear encrypted key: ${key}`,
        error
      );
    }
  }

  // sessionStorageもクリア
  try {
    sessionStorage.clear();
    logger.info('[StorageUtils] SessionStorage cleared');
  } catch (error) {
    logger.error('[StorageUtils] Failed to clear sessionStorage:', error);
  }

  logger.info(`[StorageUtils] Cleared ${clearedCount} encrypted storage keys`);
  return clearedCount;
}

/**
 * 強制再ログインをトリガー
 * Issue #1278: Device ID変更時に呼び出し
 *
 * @param signOutCallback - サインアウト処理のコールバック（Clerk.signOut等）
 */
export async function triggerForceRelogin(
  signOutCallback?: () => Promise<void>
): Promise<void> {
  logger.warn(
    '[StorageUtils] Force re-login triggered due to device fingerprint change'
  );

  // 1. 暗号化データをクリア
  clearAllEncryptedData();

  // 2. マイグレーションフラグをリセット
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(NEW_STORAGE_KEYS.MIGRATION_USER_SCOPE_V1)) {
      localStorage.removeItem(key);
    }
  }

  // 3. サインアウト実行
  if (signOutCallback) {
    try {
      await signOutCallback();
      logger.info('[StorageUtils] Sign out completed');
    } catch (error) {
      logger.error('[StorageUtils] Sign out failed:', error);
      // サインアウト失敗時もページリロードで強制ログアウト
      window.location.href = '/login?reason=device_changed';
    }
  } else {
    // コールバックがない場合はページリロード
    window.location.href = '/login?reason=device_changed';
  }
}
